import { type RouteProp, useRoute } from "@react-navigation/native";
import { CheckCircle, Home, MapPin, Plus, Undo2 } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useState } from "react";
import { ScrollView, View, XStack, YStack } from "tamagui";
import { TKey } from "../../../shared/translations/translationKeys";
import { ModuleState } from "../../../templates/types";
import { useApi } from "../../core/apis";
import {
  HelperType,
  type HelpItem,
  HelpItemRequiredType,
} from "../../helper/HelperManager";
import { useHelper } from "../../helper/useHelper";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { ModuleBlock } from "../../ui/Block";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import type { InfoRowItemProps } from "../../ui/InfoRow";
import type { IconProp } from "../../ui/icons";
import { Label } from "../../ui/Label";
import { LoadingCircular } from "../../ui/LoadingCircular";
import { log } from "../../ui/log";
import { Title } from "../../ui/Title";
import { ModuleDataType } from "../core/BaseModuleDataModel";
import { DoneBox } from "../elements/DoneBox";
import { Location } from "./elements/Location";
import { LocationDoneSheet } from "./elements/LocationDoneSheet";
import { LocationList } from "./elements/LocationList";
import {
  type LocationWithMapData,
  type MapHandler,
  useMap,
} from "./elements/Map";
import { MovePin } from "./elements/MovePin";
import { PlacesAutoCompleteInput } from "./elements/PlacesAutoCompleteInput";
import type LocationModel from "./LocationModel";
import type { LocationModuleModelData } from "./LocationModel";
import type LocationModuleModel from "./LocationModuleModel";

export function useNavigateToLocation() {
  // biome-ignore lint/suspicious/noExplicitAny: Går säkert att fixa?
  const { params } = useRoute<RouteProp<any>>();
  const navigation = usePlanNavigation();

  return React.useCallback(
    (
      locationItem: Pick<
        LocationWithMapData,
        "planUid" | "dataUid" | "moduleUid"
      >,
    ) => {
      if (params?.dataUid) {
        navigation.replace("Module", {
          planUid: locationItem.planUid,
          moduleUid: locationItem.moduleUid,
          dataUid: locationItem.dataUid,
        });
      } else {
        navigation.push("Module", {
          planUid: locationItem.planUid,
          moduleUid: locationItem.moduleUid,
          dataUid: locationItem.dataUid,
        });
      }
    },
    [params, navigation],
  );
}

const SpinnerIcon: IconProp = () => {
  return <LoadingCircular />;
};

export const LocationModuleStaticUI: React.FC<{ module: LocationModuleModel }> =
  observer(({ module }) => {
    const { modulesDataApi } = useApi();
    const isDone = module.isDone;

    const onMapClick = useCallback(
      (map: MapHandler, latLng: google.maps.LatLngLiteral) => {
        if (map?.items.length && !isDone) {
          const firstItem = map.items[0];
          map.setItems([{ ...firstItem, mapData: latLng }]);
        }
      },
      [isDone],
    );

    useEffect(() => {
      const unsub = modulesDataApi.watchDataForModuleUid(module.uid);
      return () => {
        log.network(
          `Stopped watchDataForModuleUid from moduleUid ${module.uid}`,
        );
        unsub();
      };
    }, [modulesDataApi, module.uid]);

    const { map, mapRef } = useMap(undefined, onMapClick);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (!map) {
        return;
      }

      const firstItem = module.locationItems[0];

      if (isDone && firstItem?.data.mapData) {
        map.setItems([
          {
            label: firstItem.data.label,
            mapData: firstItem.data.mapData,
            planUid: module.plan.uid,
            moduleUid: module.uid,
            dataUid: firstItem.uid,
          },
        ]);
      }

      map.renderMarkers(true);
    }, [isDone, map, module.locationItems[0], module.plan.uid, module.uid]);

    const handleSearchLocation = useCallback(
      async (location: google.maps.places.PlacePrediction) => {
        const user = module.plan.userInPlan;

        if (!map || !location || !user) {
          return;
        }

        setLoading(true);

        const place = location.toPlace(); // Get first predicted place.
        await place.fetchFields({
          fields: ["displayName", "formattedAddress", "location"],
        });

        const placeLocation = place.location;
        if (placeLocation) {
          const mapData = {
            lat: placeLocation.lat(),
            lng: placeLocation.lng(),
          };

          map.setItems([
            {
              label: place.displayName ?? "",
              mapData,
              planUid: module.plan.uid,
              moduleUid: module.uid,
            },
          ]);

          map.renderMarkers(true);
        }

        setLoading(false);
      },
      [module, map],
    );

    const handleSave = useCallback(async () => {
      const user = module.plan.userInPlan;

      if (!map || !user || !map.items.length) {
        return;
      }

      const firstItem = map.items[0];

      setLoading(true);
      let item: LocationModel = module.locationItems[0];

      if (item) {
        await item.updateItem({
          label: firstItem.label,
          mapData: firstItem.mapData,
        });
      } else {
        item = (await module.addNewData<LocationModuleModelData>(
          ModuleDataType.Location,
          {
            label: firstItem.label,
            userId: user.uid,
            mapData: firstItem.mapData,
          },
          TKey.ActivityLocationAdded,
          { label: firstItem.label },
        )) as LocationModel;
      }

      module.setDone([item.uid]);

      setLoading(false);
    }, [
      map,
      map?.items[0],
      module.plan.userInPlan,
      module.locationItems[0],
      module,
    ]);

    const locationItem = isDone ? module.locationItems[0] : undefined;

    return (
      <View>
        <View aspectRatio={5 / 2} width={"100%"} ref={mapRef} />
        {module.isDone ? undefined : (
          <View marginBottom={"$space.medium"}>
            <PlacesAutoCompleteInput
              disabled={loading}
              placeholder={loading ? "Searching..." : "Search location..."}
              borderBottomWidth={1}
              blurOnSubmit={false}
              icon={loading ? SpinnerIcon : Plus}
              submitOnEnter
              type="padded"
              onSelect={handleSearchLocation}
            />
          </View>
        )}

        {locationItem?.data.label || map?.items[0]?.label ? (
          <Box>
            <Title>{map?.items[0]?.label || locationItem?.data.label}</Title>
          </Box>
        ) : undefined}

        {isDone ? (
          <Button
            type="secondary"
            onPress={module.toggleDoneState.bind(module)}
            icon={Undo2}
          >
            Revert done status
          </Button>
        ) : (
          <Button
            disabled={!map?.items.length}
            onPress={handleSave}
            icon={CheckCircle}
          >
            Save location
          </Button>
        )}
      </View>
    );
  });

export const LocationModuleUI = observer(
  ({ module }: { module: LocationModuleModel }) => {
    // biome-ignore lint/suspicious/noExplicitAny: Går säkert att fixa?
    const { params } = useRoute<RouteProp<any>>();
    const { navigate } = usePlanNavigation();
    const moduleUid = module.uid;
    const dataUid = params?.dataUid;

    const navigateToLocation = useNavigateToLocation();

    const { map, mapRef } = useMap(
      React.useCallback(
        (
          locationItem: Pick<
            LocationWithMapData,
            "planUid" | "dataUid" | "moduleUid"
          >,
        ) => {
          navigateToLocation(locationItem);
        },
        [navigateToLocation],
      ),
    );
    const { modulesDataApi } = useApi();

    React.useEffect(() => {
      const unsub = modulesDataApi.watchDataForModuleUid(moduleUid);
      return () => {
        log.network(
          `Stopped watchDataForModuleUid from moduleUid ${moduleUid}`,
        );
        unsub();
      };
    }, [modulesDataApi, moduleUid]);

    useEffect(() => {
      if (map && module.mapDataItems.length) {
        map.setItems(
          module.mapDataItems
            .filter(
              (item) =>
                !module.isDone ||
                module.data.selectedLocations?.includes(item.uid),
            )
            .map((item) => ({
              planUid: item.plan.uid,
              moduleUid: module.uid,
              dataUid: item.uid,
              label: item.data.label,
              mapData: item.data.mapData,
            })),
        );
      }
    }, [
      map,
      module.uid,
      module.mapDataItems,
      module.isDone,
      module.data.selectedLocations,
    ]);

    const locationItems = module.locationItems.filter(
      (locationItem) =>
        !module.isDone ||
        module.data.selectedLocations?.includes(locationItem.uid),
    );
    const locationItem =
      dataUid && locationItems.find((item) => item.uid === dataUid);

    const items: HelpItem[] = React.useMemo(
      () => [
        {
          type: HelpItemRequiredType.Required,
          label: "Add one or more locations below to enable voting",
          isCompleted: () => {
            return locationItems.length > 0;
          },
        },
        {
          type: HelpItemRequiredType.Required,
          label: "Vote for your favorite locations",
          isCompleted: () => {
            return module.hasVoted;
          },
        },
        {
          type: HelpItemRequiredType.Required,
          label: "All have casted their votes! Decide the final location(s)!",
          isEnabled: () => {
            return (
              module.waitingForUsers.length === 0 && module.plan.usersCount > 1
            );
          },
          isCompleted: () => {
            return module.data.state === ModuleState.Done;
          },
        },
        {
          type: HelpItemRequiredType.Tip,
          label: "You can add a description to each location",
          isEnabled: () => {
            return !locationItems.every((f) => f.hasDescription);
          },
          isCompleted: () => {
            return false;
          },
        },
      ],
      [
        module.hasVoted,
        module.data.state,
        module.waitingForUsers,
        module.plan.usersCount,
        locationItems,
      ],
    );

    const helper = useHelper(module.uid, HelperType.Location, items);

    return (
      <>
        {helper}
        <YStack gap="$medium">
          <View position="relative">
            {map && locationItem && (
              <MovePin map={map} locationItem={locationItem} />
            )}
            <View aspectRatio={5 / 2} width={"100%"} ref={mapRef} />
          </View>

          {locationItems.length > 1 ? (
            <ScrollView
              horizontal
              backgroundColor={"$backgroundBlock"}
              padding="$space.larger"
            >
              <XStack alignItems="center" gap="$large">
                <XStack
                  cursor="pointer"
                  onPress={() =>
                    navigate("Module", {
                      planUid: module.plan.uid,
                      moduleUid: module.uid,
                    })
                  }
                  alignItems="center"
                  gap="$small"
                >
                  <Home size={"$small"} />
                  <Label>Overview</Label>
                </XStack>
                {locationItems.map((location) => {
                  const TextComponent = location.uid === dataUid ? Label : Body;
                  return (
                    <TextComponent
                      key={location.uid}
                      cursor="pointer"
                      onPress={() => {
                        navigateToLocation({
                          dataUid: location.uid,
                          planUid: location.plan.uid,
                          moduleUid: module.uid,
                        });
                      }}
                    >
                      {location.data.label}
                    </TextComponent>
                  );
                })}
              </XStack>
            </ScrollView>
          ) : undefined}

          <View>
            {map ? (
              dataUid ? (
                <Location module={module} dataUid={dataUid} map={map} />
              ) : map && module.isDone && locationItems.length === 1 ? (
                <Location
                  module={module}
                  dataUid={locationItems[0].data.uid}
                  map={map}
                />
              ) : (
                <LocationList map={map} module={module} />
              )
            ) : undefined}
          </View>

          {!module.isDone &&
          locationItems.length &&
          module.waitingForUsers.length ? (
            <Box>
              <Body fontStyle="italic">
                Waiting for ratings from {module.waitingForUsers.join(", ")}
              </Body>
            </Box>
          ) : undefined}

          {module.locationItems.length ? (
            <DoneBox
              ctaLabel={"Decide on final locations"}
              userIsDone={module.hasVoted}
              waitingForUsers={module.waitingForUsers}
              module={module}
              userReadyUpDescription="Rate the current locations or add your own"
              beforeDoneDescription="When everyone is ready, decide on the the final location(s) to set this step as finished."
              everyoneReadyDescription="Everyone has voted, select the final location(s) to set this step as finished."
              doneDescription="Great! You have decided on location(s) together"
            />
          ) : undefined}
          <LocationDoneSheet module={module} />
        </YStack>
      </>
    );
  },
);

export const LocationModuleUIBlockUI = observer(
  ({ module }: { module: LocationModuleModel }) => {
    const infoRowItems = React.useMemo<InfoRowItemProps[]>(
      () => [
        {
          text: `${module.data.snapshot.numLocations ?? 0} locations`,
          icon: MapPin,
        },
      ],
      [module.data.snapshot],
    );

    return (
      <ModuleBlock
        module={module}
        infoRowItems={infoRowItems}
        doneText={module.data.snapshot.selectedLocations}
      />
    );
  },
);

export const LocationStaticModuleUIBlockUI = observer(
  ({ module }: { module: LocationModuleModel }) => {
    const infoRowItems = React.useMemo<InfoRowItemProps[]>(
      () => [
        {
          text: "Tap to select location",
          icon: MapPin,
        },
      ],
      [],
    );

    return (
      <ModuleBlock
        module={module}
        infoRowItems={!module.isDone ? infoRowItems : []}
        doneText={module.data.snapshot.selectedLocations}
      />
    );
  },
);
